import { Button } from "../../../../components/ui";
import {
  CheckBox,
  DateRangePicker,
  TextInput,
} from "../../../../components/ui/inputs";
import { faSearch } from "@fortawesome/free-solid-svg-icons";
import { useRef, useState } from "react";
import { formatDate } from "../../../../utils/dateTimeHelper";
import localization from "../../../../config/localization";

export default function TransactionFilters({
  statusOptions,
  filters,
  onFiltersChange,
  onApply,
}: {
  statusOptions: string[];
  filters: {
    searchText: string | null;
    fromDate: string | null;
    toDate: string | null;
    statuses: string[];
  };
  onFiltersChange: (updated: Partial<typeof filters>) => void;
  onApply: (finalFilters: {
    searchText: string | null;
    fromDate: string | null;
    toDate: string | null;
    statuses: string[];
  }) => void;
}) {
  const debounceRef = useRef<NodeJS.Timeout | null>(null);
  const [isFiltersVisible, setIsFilterPanelOpen] = useState(false);
  const [appliedFilters, setAppliedFilters] = useState<typeof filters | null>(
    null
  );
  const [showFilter, setShowFilter] = useState(false);

  const onStatusChange = (status: string) => {
    const newStatuses = filters.statuses.includes(status)
      ? filters.statuses.filter((s) => s !== status)
      : [...filters.statuses, status];

    onFiltersChange({ statuses: newStatuses });
  };

  const onDateRangeChangeSelect = (
    fromDate: string | null,
    toDate: string | null
  ) => {
    onFiltersChange({ fromDate, toDate });
  };

  const onApplyClick = () => {
    setIsFilterPanelOpen(false);
    setAppliedFilters(filters);
    onApply(filters);
  };

  const onSearchChange = (value: string | null) => {
    onFiltersChange({ searchText: value });

    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    if (value == null || value.trim() === "") {
      debounceRef.current = setTimeout(() => {
        onApply({
          ...filters,
          searchText: value,
        });
      }, 1000);
    }
  };

  const onSearchClick = (value?: string | null) => {
    onFiltersChange({ searchText: value });
    onApply({
      ...filters,
      searchText: value || null,
    });
  };

  const resetFilters = () => {
    const clearedFilters = {
      searchText: null,
      fromDate: null,
      toDate: null,
      statuses: [],
    };
    onFiltersChange(clearedFilters);
    setIsFilterPanelOpen(false);
    setAppliedFilters(null);
    onApply(clearedFilters);
  };

  return (
    <div className={`relative ${showFilter ? "mb-0" : "mb-1"}`}>
      {!showFilter && (
        <div
          className="2xl:hidden absolute right-0 top-[-44px] cursor-pointer"
          onClick={() => setShowFilter(!showFilter)}
        >
          <div className="bg-[#232323] border-[#232323] rounded-full size-8 flex items-center justify-center">
            <img
              src={"/icons/ic-casino-search.svg"}
              alt="Search"
              className="size-4"
            />
          </div>
        </div>
      )}

      <div className={`${showFilter ? "block" : "hidden"} 2xl:block mb-2`}>
        <div className="flex gap-x-2 my-2 items-center 2xl:mb-0 2xl:absolute 2xl:right-0 2xl:top-[-60px] cursor-pointer">
          <div className="w-10/12 lg:w-11/12">
            <TextInput
              placeholder={localization.searchtransactionid}
              value={filters.searchText || ""}
              type="text"
              faIcon={faSearch}
              className="!mb-0"
              inputClassName="text-base font-medium bg-[#232323] text-[#A9A9A9] placeholder-[#A9A9A9] rounded-xl p-3 !h-10"
              faIconClassName="text-white"
              onChange={(e: any) => onSearchChange(e.target.value)}
              onButtonClick={(value) => onSearchClick(value)}
              showBorder={false}
              isFocusVisible={false}
            />
          </div>
          <div>
            <Button
              className={`${
                isFiltersVisible ? "bg-white " : "bg-[#232323]"
              } w-full rounded-xl px-3 h-10 cursor-pointer float-right`}
              onClick={() => setIsFilterPanelOpen((prev) => !prev)}
            >
              <div
                className={`flex justify-center gap-1 font-medium ${
                  isFiltersVisible
                    ? "bg-white text-black "
                    : "bg-[#232323] text-[#A9A9A9]"
                }`}
              >
                <img
                  src={
                    isFiltersVisible
                      ? "/icons/ic-filter-black.svg"
                      : "/icons/ic-filter-white.svg"
                  }
                  alt="Filter Icon"
                />
                <span>{localization.filter}</span>
              </div>
            </Button>
          </div>

          {showFilter && (
            <div
              className={`${showFilter ? "block" : "hidden"} 2xl:hidden`}
              onClick={() => setShowFilter(!showFilter)}
            >
              <div className="bg-[#232323] border-[#232323] rounded-full size-8 flex items-center justify-center">
                <img
                  src={"/icons/ic-cancel-filter.svg"}
                  alt="cancel"
                  className="size-4"
                />
              </div>
            </div>
          )}
        </div>

        {isFiltersVisible && (
          <div>
            <div
              className={`w-full bg-[#111111] border border-[#242424] rounded-lg p-3 cursor-pointer 2xl:mt-2`}
            >
              {statusOptions.length > 0 && (
                <div>
                  <span>{localization.history_select_status}</span>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 2xl:grid-cols-8 gap-2 my-2">
                    {statusOptions.map((status) => (
                      <CheckBox
                        key={status}
                        label={status}
                        checked={filters.statuses.includes(status)}
                        onChange={() => onStatusChange(status)}
                      />
                    ))}
                  </div>
                </div>
              )}

              <div>
                <DateRangePicker
                  placeholder={localization.select_date}
                  startDate={filters.fromDate}
                  endDate={filters.toDate}
                  onRangeChange={(start, end) => {
                    onDateRangeChangeSelect(start, end);
                  }}
                  disableFutureDates={true}
                />
              </div>

              <div>
                <Button
                  disabled={
                    filters.statuses.length === 0 &&
                    (filters.fromDate == null || filters.toDate == null)
                  }
                  className="bg-primary-gradient rounded-md p-2 font-medium w-1/4 lg:w-2/12 2xl:w-1/12 cursor-pointer disabled:cursor-not-allowed disabled:opacity-50 "
                  onClick={onApplyClick}
                >
                  {localization.apply}
                </Button>
              </div>
            </div>
          </div>
              )}

        {appliedFilters && (
          <div className="w-full bg-[#111111] border border-[#242424] rounded-lg p-3 cursor-pointer my-2">
            <div className="flex justify-between">
              <div className="grid gap-y-2">
                {appliedFilters?.statuses?.length > 0 && (
                  <span className="text-sm font-medium truncate">
                    {appliedFilters.statuses.join(", ")}
                  </span>
                )}

                {appliedFilters?.fromDate && appliedFilters?.toDate && (
                  <div className="flex items-center gap-x-2 text-sm font-medium truncate">
                    <img
                      src={"/icons/ic-white-calendar.svg"}
                      alt="calendar"
                      className="size-4"
                    />
                    <span>{formatDate(appliedFilters.fromDate)}</span>
                    <img
                      src={"/icons/ic-right-arrow.svg"}
                      alt="right-arrow"
                      className="size-4"
                    />
                    <span>{formatDate(appliedFilters.toDate)}</span>
                  </div>
                )}
              </div>

              <img
                src={"/icons/ic-x-circle.svg"}
                alt="Clear filter"
                className="size-4 cursor-pointer hover:opacity-80"
                onClick={resetFilters}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
